import { generatePDF } from '../../../helper/pdfGenerator';
import { PDFPage, PDFResult, PDFText } from '../../../models/pdfModal';

const LINE_HEIGHT = 5.25;
const FONT_SIZE = 12;

interface RiskManagementText {
  text: string;
  numOfLine?: number;
}
interface RiskManagementArea {
  area: RiskManagementText;
  description: RiskManagementText[];
}
interface RiskManagementDetails {
  id: string;
  title: RiskManagementText;
  area: RiskManagementArea[];
}

const riskManagementDetails: RiskManagementDetails[] = [
  {
    id: 'liquidation',
    title: { text: 'Liquidation Risk' },
    area: [
      {
        area: { text: 'Green area (not significant)' },
        description: [
          {
            text: 'Liquidity risk is the risk that an individual or firm will have difficulty selling an asset without incurring a loss. That is, there may be a lack of interest in the market for a particular asset, forcing the owner to sell it for less than its actual value.',
            numOfLine: 4,
          },
          {
            text: 'The Company maintains a level of cash and cash equivalents and bank facilities deemed adequate by management to ensure, as far as possible, that it will have sufficient liquidity to meet its liabilities when they fall due.',
            numOfLine: 3,
          },
        ],
      },
      {
        area: { text: 'Yellow area (not significant)' },
        description: [
          {
            text: 'Liquidity risk is the risk that an individual or firm will have difficulty selling an asset without incurring a loss. That is, there may be a lack of interest in the market for a particular asset, forcing the owner to sell it for less than its actual value.',
            numOfLine: 4,
          },
          {
            text: 'The Company has strengthened cash flow forecasting and reduce short-term debt reliance to ensure, as far as possible, that it will have sufficient liquidity to meet its liabilities when they fall due.',
            numOfLine: 3,
          },
        ],
      },
      {
        area: { text: 'Orange area / Red area (Significant)' },
        description: [
          {
            text: 'Liquidity risk is the risk that an individual or firm will have difficulty selling an asset without incurring a loss. That is, there may be a lack of interest in the market for a particular asset, forcing the owner to sell it for less than its actual value.',
            numOfLine: 4,
          },
          {
            text: 'The Company has activated contingency funding plan and prioritise critical payments to ensure, as far as possible, that it will have sufficient liquidity to meet its liabilities when they fall due.',
            numOfLine: 3,
          },
        ],
      },
      {
        area: { text: 'Not applicable' },
        description: [
          {
            text: 'The Company maintains sufficient cash balances to support its daily operations.',
            numOfLine: 2,
          },
        ],
      },
    ],
  },
  {
    id: 'funding',
    title: { text: 'Funding Risk' },
    area: [
      {
        area: { text: 'Green area (not significant)' },
        description: [
          {
            text: 'Funding risk is the possibility that a business or individual might not have access to the financing it needs at an affordable rate.',
            numOfLine: 2,
          },
          {
            text: 'The Company maintains strong credit profile to ensure that it will have sufficient solvency to meet its liabilities when they fall due.',
            numOfLine: 2,
          },
        ],
      },
      {
        area: { text: 'Yellow area (not significant)' },
        description: [
          {
            text: 'Funding risk is the possibility that a business or individual might not have access to the financing it needs at an affordable rate.',
            numOfLine: 2,
          },
          {
            text: 'The Company has extended debt maturities and increase funding diversification, to ensure that it will have sufficient solvency to meet its liabilities when they fall due.',
            numOfLine: 2,
          },
        ],
      },
      {
        area: { text: 'Orange area / Red area (Significant)' },
        description: [
          {
            text: 'Funding risk is the possibility that a business or individual might not have access to the financing it needs at an affordable rate.',
            numOfLine: 2,
          },
          {
            text: 'The Company has restructured or refinanced debt to ensure that it will have sufficient solvency to meet its liabilities when they fall due.',
            numOfLine: 2,
          },
        ],
      },
      {
        area: { text: 'Not applicable' },
        description: [
          {
            text: 'The Company maintains sufficient cash balances to maintain its solvency.',
            numOfLine: 2,
          },
        ],
      },
    ],
  },
  {
    id: 'credit',
    title: { text: 'Credit Risk' },
    area: [
      {
        area: { text: 'Green area (not significant)' },
        description: [
          {
            text: 'Credit risk, or default risk, is the risk that a financial loss will be incurred if a counterpart to a (derivatives) transaction does not fulfil its financial obligations in a timely manner.',
            numOfLine: 3,
          },
          {
            text: 'The Company does not have any major concentration of credit risk related to any individual customer or counterparty. The Company has regularly perform due diligence and credit scoring for their customer or counterparty.',
            numOfLine: 3,
          },
        ],
      },
      {
        area: { text: 'Yellow area (not significant)' },
        description: [
          {
            text: 'Credit risk, or default risk, is the risk that a financial loss will be incurred if a counterpart to a (derivatives) transaction does not fulfil its financial obligations in a timely manner.',
            numOfLine: 3,
          },
          {
            text: 'The Company does not have any major concentration of credit risk related to any individual customer or counterparty. The Company has tighten lending or credit standards and monitor watchlist exposures for those customer or counterparty which possibly become major part of concentration of credit risk.',
            numOfLine: 4,
          },
        ],
      },
      {
        area: { text: 'Orange area / Red area (Significant)' },
        description: [
          {
            text: 'Credit risk, or default risk, is the risk that a financial loss will be incurred if a counterpart to a (derivatives) transaction does not fulfil its financial obligations in a timely manner.',
            numOfLine: 3,
          },
          {
            text: 'The Company does not have any major concentration of credit risk related to any individual customer or counterparty. The Company will commence a legal proceeding against the customer who fails to pay after the Company initiates the debt recovery process.',
            numOfLine: 4,
          },
        ],
      },
      {
        area: { text: 'Not applicable' },
        description: [
          {
            text: 'There are no significant changes in credit risk as compared to prior periods.',
          },
        ],
      },
    ],
  },
  {
    id: 'commodity',
    title: { text: 'Commodity Risk' },
    area: [
      {
        area: { text: 'Green area (not significant)' },
        description: [
          {
            text: "Commodity risk is the potential for changes in commodity prices to result in losses. Commodity prices tend to be volatile and can change rapidly over the short term. It's also possible for prices to shift higher or lower for long periods of time, a phenomenon known as a commodities super cycle. Many industries take commodities as a basic input and are highly sensitive to changes in prices.",
            numOfLine: 6,
          },
          {
            text: 'The Company has monitored market trends and locked in long-term stable contracts to ensure its commodity price risk exposure is at a fixed rate.',
            numOfLine: 2,
          },
        ],
      },
      {
        area: { text: 'Yellow area (not significant)' },
        description: [
          {
            text: "Commodity risk is the potential for changes in commodity prices to result in losses. Commodity prices tend to be volatile and can change rapidly over the short term. It's also possible for prices to shift higher or lower for long periods of time, a phenomenon known as a commodities super cycle. Many industries take commodities as a basic input and are highly sensitive to changes in prices.",
            numOfLine: 6,
          },
          {
            text: 'The Company has built strategic inventory reserves to ensure its commodity price risk exposure is at a fixed rate. This is achieved by entering into commodity swaps and options as hedges of the variability in cash flows attributable to movements in commodity prices.',
            numOfLine: 4,
          },
        ],
      },
      {
        area: { text: 'Orange area / Red area (Significant)' },
        description: [
          {
            text: "Commodity risk is the potential for changes in commodity prices to result in losses. Commodity prices tend to be volatile and can change rapidly over the short term. It's also possible for prices to shift higher or lower for long periods of time, a phenomenon known as a commodities super cycle. Many industries take commodities as a basic input and are highly sensitive to changes in prices.",
            numOfLine: 6,
          },
          {
            text: 'The Company has executed aggressive hedging or insurance to ensure its commodity price risk exposure is at a fixed rate. This is achieved by entering into commodity swaps and options as hedges of the variability in cash flows attributable to movements in commodity prices.',
            numOfLine: 4,
          },
        ],
      },
      {
        area: { text: 'Not applicable' },
        description: [
          {
            text: 'The Company does not exposed to commodity price risk.',
            numOfLine: 1,
          },
        ],
      },
    ],
  },
  {
    id: 'foreign-currency',
    title: { text: 'Foreign Currency Risk' },
    area: [
      {
        area: { text: 'Green area (not significant)' },
        description: [
          {
            text: 'Foreign exchange risk refers to the losses that an international financial transaction may incur due to currency fluctuations.',
            numOfLine: 2,
          },
          {
            text: 'The Company has performed natural hedge on transactions or balances denominated in foreign currencies by matching inflows and outflows.',
            numOfLine: 2,
          },
        ],
      },
      {
        area: { text: 'Yellow area (not significant)' },
        description: [
          {
            text: 'Foreign exchange risk refers to the losses that an international financial transaction may incur due to currency fluctuations.',
            numOfLine: 2,
          },
          {
            text: 'The Company has performed natural hedge on transactions or balances denominated in foreign currencies. This has been done by diversifying currency base to mitigate risk.',
            numOfLine: 3,
          },
        ],
      },
      {
        area: { text: 'Orange area / Red area (Significant)' },
        description: [
          {
            text: 'Foreign exchange risk refers to the losses that an international financial transaction may incur due to currency fluctuations.',
            numOfLine: 2,
          },
          {
            text: 'The Company has implemented full hedge program on transactions or balances denominated in foreign currencies. This has been done by renegotiate contracts in home currency.',
            numOfLine: 3,
          },
        ],
      },
      {
        area: { text: 'Not applicable' },
        description: [
          {
            text: 'The Company does not have any transactions or balances denominated in foreign currencies and hence, is not exposed to foreign currency risk.',
            numOfLine: 2,
          },
        ],
      },
    ],
  },
  {
    id: 'interest',
    title: { text: 'Interest Rate Risk' },
    area: [
      {
        area: { text: 'Green area (not significant)' },
        description: [
          {
            text: 'The interest rate risk, or market risk, refers to the chance that investments in bonds will suffer as the result of unexpected interest rate changes.',
            numOfLine: 2,
          },
          {
            text: "The Company will consider entering into derivative financial instruments where necessary to achieve an appropriate mix of fixed and floating rate exposure within the Company's policy.",
            numOfLine: 3,
          },
        ],
      },
      {
        area: { text: 'Yellow area (not significant)' },
        description: [
          {
            text: 'The interest rate risk, or market risk, refers to the chance that investments in bonds will suffer as the result of unexpected interest rate changes.',
            numOfLine: 2,
          },
          {
            text: 'The Company has rebalanced its portfolio to mitigate the interest rate risk. This is achieved by entering into interest rates swaps and options as hedges of the variability in cash flows attributable to movements in interest rate.',
            numOfLine: 3,
          },
        ],
      },
      {
        area: { text: 'Orange area / Red area (Significant)' },
        description: [
          {
            text: 'The interest rate risk, or market risk, refers to the chance that investments in bonds will suffer as the result of unexpected interest rate changes.',
            numOfLine: 2,
          },
          {
            text: 'The Company has executed aggressive hedging or insurance to mitigate the interest rate risk. This is achieved by entering into interest rates swaps and options as hedges of the variability in cash flows attributable to movements in interest rate.',
            numOfLine: 4,
          },
        ],
      },
      {
        area: { text: 'Not applicable' },
        description: [
          {
            text: 'The Company does not have loans that has exposure to interest rate risk.',
            numOfLine: 1,
          },
        ],
      },
    ],
  },
  {
    id: 'operational',
    title: { text: 'Operational Risk' },
    area: [
      {
        area: { text: 'Green area (not significant)' },
        description: [
          {
            text: 'Operational risk is the prospect of loss resulting from inadequate or failed procedures, human error, systems or policies.',
            numOfLine: 2,
          },
          {
            text: 'We are committed to good governance and responsible business practices. Our company promotes equality, respects diversity, and has zero tolerance for gender inequality, discrimination, or sexual harassment. We also take confidentiality and data protection seriously, ensuring compliance with relevant laws and safeguarding the privacy of everyone we work with.',
            numOfLine: 5,
          },
          {
            text: 'The Company has provided proper staff training and basic internal control in order to eliminate exposures and ensure successful responses.',
            numOfLine: 2,
          },
        ],
      },
      {
        area: { text: 'Yellow area (not significant)' },
        description: [
          {
            text: 'Operational risk is the prospect of loss resulting from inadequate or failed procedures, human error, systems or policies.',
            numOfLine: 2,
          },
          {
            text: 'We are committed to good governance and responsible business practices. Our company promotes equality, respects diversity, and has zero tolerance for gender inequality, discrimination, or sexual harassment. We also take confidentiality and data protection seriously, ensuring compliance with relevant laws and safeguarding the privacy of everyone we work with.',
            numOfLine: 5,
          },
          {
            text: 'The Company has strengthened IT security and increases internal audit in order to eliminate exposures and ensure successful responses.',
            numOfLine: 2,
          },
        ],
      },
      {
        area: { text: 'Orange area / Red area (Significant)' },
        description: [
          {
            text: 'Operational risk is the prospect of loss resulting from inadequate or failed procedures, human error, systems or policies.',
            numOfLine: 2,
          },
          {
            text: 'We are committed to good governance and responsible business practices. Our company promotes equality, respects diversity, and has zero tolerance for gender inequality, discrimination, or sexual harassment. We also take confidentiality and data protection seriously, ensuring compliance with relevant laws and safeguarding the privacy of everyone we work with.',
            numOfLine: 5,
          },
          {
            text: 'The Company has obtained external support to eliminate exposures and ensure successful responses.',
            numOfLine: 2,
          },
        ],
      },
      {
        area: { text: 'Not applicable' },
        description: [
          {
            text: 'The Company has minimal operational risk which does not impact much to the Company.',
            numOfLine: 2,
          },
        ],
      },
    ],
  },
  {
    id: 'economic-disaster',
    title: { text: 'Economic Disaster Risk' },
    area: [
      {
        area: { text: 'Green area (not significant)' },
        description: [
          {
            text: 'Economic disaster is a sudden, calamitous event that seriously disrupts the functioning of an organisation or individual and causes human, material, and economic or environmental losses that exceed the organisation or individual ability to cope using its own resources.',
            numOfLine: 4,
          },
          {
            text: 'The Company maintains a level of cash and cash equivalents and bank facilities deemed adequate by management to minimise risk.',
            numOfLine: 2,
          },
        ],
      },
      {
        area: { text: 'Yellow area (not significant)' },
        description: [
          {
            text: 'Economic disaster is a sudden, calamitous event that seriously disrupts the functioning of an organisation or individual and causes human, material, and economic or environmental losses that exceed the organisation or individual ability to cope using its own resources.',
            numOfLine: 4,
          },
          {
            text: 'The Company has strengthened IT scenario planning, implement flexible budgeting and cost optimization to mitigate risk.',
            numOfLine: 2,
          },
        ],
      },
      {
        area: { text: 'Orange area / Red area (Significant)' },
        description: [
          {
            text: 'Economic disaster is a sudden, calamitous event that seriously disrupts the functioning of an organisation or individual and causes human, material, and economic or environmental losses that exceed the organisation or individual ability to cope using its own resources.',
            numOfLine: 4,
          },
          {
            text: 'The Company has activated business continuity plan, implement strategic restructuring and divestment to mitigate risk.',
            numOfLine: 2,
          },
        ],
      },
      {
        area: { text: 'Not applicable' },
        description: [
          {
            text: 'The Company does not have economic disaster risk.',
            numOfLine: 2,
          },
        ],
      },
    ],
  },
  {
    id: 'legal',
    title: { text: 'Legal Risk' },
    area: [
      {
        area: { text: 'Green area (not significant)' },
        description: [
          {
            text: 'Legal risk refers to the potential for financial loss or harm to an organization due to legal or regulatory actions, including lawsuits, fines, or other penalties.',
            numOfLine: 2,
          },
          {
            text: 'The Company has standardized legal reviews to minimise risk.',
            numOfLine: 1,
          },
        ],
      },
      {
        area: { text: 'Yellow area (not significant)' },
        description: [
          {
            text: 'Legal risk refers to the potential for financial loss or harm to an organization due to legal or regulatory actions, including lawsuits, fines, or other penalties.',
            numOfLine: 2,
          },
          {
            text: 'The Company has increased external legal counsel involvement, tighten intellectual property and contract management to mitigate risk.',
            numOfLine: 2,
          },
        ],
      },
      {
        area: { text: 'Orange area / Red area (Significant)' },
        description: [
          {
            text: 'Legal risk refers to the potential for financial loss or harm to an organization due to legal or regulatory actions, including lawsuits, fines, or other penalties.',
            numOfLine: 2,
          },
          {
            text: 'The Company has implemented emergency settlements and crisis communication with stakeholders to mitigate risk.',
            numOfLine: 2,
          },
        ],
      },
      {
        area: { text: 'Not applicable' },
        description: [
          {
            text: 'The Company has minimal legal risk which does not impact much to the Company.',
            numOfLine: 2,
          },
        ],
      },
    ],
  },
  {
    id: 'compliance',
    title: { text: 'Compliance Risk' },
    area: [
      {
        area: { text: 'Green area (not significant)' },
        description: [
          {
            text: 'Compliance risk refers to the possibility of an organization facing legal penalties, financial losses, or reputational damage due to non-compliance with laws, regulations, industry standards, or internal policies.',
            numOfLine: 3,
          },
          {
            text: 'The Company has regularly update policies and provided proper staff training and compliance monitoring to minimise the risk.',
            numOfLine: 2,
          },
        ],
      },
      {
        area: { text: 'Yellow area (not significant)' },
        description: [
          {
            text: 'Compliance risk refers to the possibility of an organization facing legal penalties, financial losses, or reputational damage due to non-compliance with laws, regulations, industry standards, or internal policies.',
            numOfLine: 3,
          },
          {
            text: 'The Company has strengthened compliance audits, increased reporting and monitoring systems to mitigate risk.',
            numOfLine: 2,
          },
        ],
      },
      {
        area: { text: 'Orange area / Red area (Significant)' },
        description: [
          {
            text: 'Compliance risk refers to the possibility of an organization facing legal penalties, financial losses, or reputational damage due to non-compliance with laws, regulations, industry standards, or internal policies.',
            numOfLine: 3,
          },
          {
            text: 'The Company has overhauled compliance framework and crisis remediation with regulators to mitigate risk.',
            numOfLine: 2,
          },
        ],
      },
      {
        area: { text: 'Not applicable' },
        description: [
          {
            text: 'The Company has minimal compliance risk which does not impact much to the Company.',
            numOfLine: 2,
          },
        ],
      },
    ],
  },
];

const generateDetailsPage = (
  details: RiskManagementDetails,
  pageX: number,
  pageY: number,
  fullWidth: number
): PDFText[] => {
  const title: PDFText = {
    id: 'title',
    value: details.title.text,
    x: pageX,
    y: (pageY += 20),
    fontsize: FONT_SIZE,
    fontStyle: 'bolditalic',
  };

  let prevHeight = (details.title.numOfLine || 1) * LINE_HEIGHT;

  const highlight = [
    { color: '#00ff00', width: 61, x: pageX + 4 },
    { color: '#ffff00', width: 62, x: pageX + 4 },
    { color: '#ff0000', width: 43, x: pageX + 38.5 },
  ];
  const areas = details.area
    .map((ar, areaInd): PDFText[] => {
      const lines = ar.area.numOfLine || 1;
      const box = highlight[areaInd];

      const texts: PDFText[] = [
        {
          id: '',
          value: `-`,
          x: pageX + 5,
          y: pageY + prevHeight,
          fontsize: FONT_SIZE,
        },
        {
          id: '',
          value: ar.area.text,
          x: pageX + 12,
          y: pageY + prevHeight,
          fontsize: FONT_SIZE,
        },
      ];

      if (box) {
        texts.splice(0, 0, {
          id: 'box',
          value: '',
          x: box.x,
          y: pageY + prevHeight - LINE_HEIGHT + 1.25,
          box: {
            height: LINE_HEIGHT,
            width: box.width,
            color: box.color,
            strokeColor: null,
          },
        });
      }

      prevHeight += lines * LINE_HEIGHT;

      const descriptions = ar.description.map((desc): PDFText => {
        const descLines = desc.numOfLine || 1;

        const description: PDFText = {
          id: `description`,
          value: desc.text,
          x: pageX + 12,
          y: pageY + prevHeight,
          fontsize: FONT_SIZE,
          maxWidth: fullWidth - 12,
          textAlign: 'justify',
        };

        prevHeight += descLines * LINE_HEIGHT;

        return description;
      });

      prevHeight += LINE_HEIGHT;

      return [...texts, ...descriptions];
    })
    .flat();

  return [title, ...areas];
};

const generateRiskManagementSummary = (): PDFPage[] => {
  const pageX: number = 25.5;
  let pageY: number = 20;
  const initialPageY: number = 20;
  const fullWidth: number = 210 - pageX * 2;

  const titleAndDescription: PDFText[] = [
    {
      id: 'formTitle',
      value: 'Summary of Risk Management and Internal Control',
      x: pageX,
      y: pageY,
      maxWidth: fullWidth,
      fontsize: FONT_SIZE,
      textAlign: 'justify',
      fontStyle: 'bold',
    },
    {
      id: 'formDescription',
      value:
        'In relation to information provided in relation to Risk Management and Internal Control as stated below, Internal Audit had also undertaken the following initiatives throughout the year :-',
      x: pageX,
      y: (pageY += 10),
      maxWidth: fullWidth,
      fontsize: FONT_SIZE,
      textAlign: 'justify',
    },
  ];

  const pages: PDFPage[] = riskManagementDetails.map(
    (details, detailsIndex): PDFPage => {
      return {
        background: '',
        texts:
          detailsIndex == 0
            ? [
                ...titleAndDescription,
                ...generateDetailsPage(details, pageX, pageY, fullWidth),
              ]
            : [...generateDetailsPage(details, pageX, initialPageY, fullWidth)],
      };
    }
  );

  const liquidation = generateDetailsPage(
    riskManagementDetails[0],
    pageX,
    pageY,
    fullWidth
  );

  return pages;
};

export const generateRiskManagementSummaryReport =
  async (): Promise<PDFResult> => {
    return await generatePDF(
      'Risk Management Summary',
      generateRiskManagementSummary()
    );
  };
